import { type Comune } from '@framework/api-client/api/inquiries/schemas/comune.ts';
import { type Provincia } from '@framework/api-client/api/inquiries/schemas/provincia.ts';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import {
  type IReference,
  ReferenceType,
} from '@framework/engine/schema/expressions/references/IReference.ts';
import {
  type IReferencePath,
  ReferencePathDirection,
} from '@framework/engine/schema/expressions/references/IReferencePath.ts';
import { getProvinceWidgetFieldNames } from './createProvinceWidgetSchema.ts';
import { type IProvinceWidget } from './IProvinceWidget.ts';

export function getProvinceWidgetSiblingReferences(
  path: IReferencePath,
  widget: IProvinceWidget,
): IReference[] {
  const fieldNames = getProvinceWidgetFieldNames(widget);

  return [
    // Valori
    {
      type: ReferenceType.Widget,
      widgetType: null,
      path,
      id: `${widget.id}-provincia`,
      code: fieldNames.provinceFieldName,
      label: `${widget.code}: provincia`,
      field: {
        type: Datatype.String,
      },
    },
    {
      type: ReferenceType.Widget,
      widgetType: null,
      path,
      id: `${widget.id}-citta`,
      code: fieldNames.townFieldName,
      label: `${widget.code}: città`,
      field: {
        type: Datatype.String,
      },
    },

    // Dati estesi provincia
    getProvinceDataReference({
      code: widget.code,
      field: 'nome',
      label: 'nome provincia',
      name: fieldNames.provinceDataFieldName,
      path,
    }),
    getProvinceDataReference({
      code: widget.code,
      field: 'lat',
      label: 'latitudine provincia',
      name: fieldNames.provinceDataFieldName,
      path,
    }),
    getProvinceDataReference({
      code: widget.code,
      field: 'lng',
      label: 'longitudine provincia',
      name: fieldNames.provinceDataFieldName,
      path,
    }),
    getProvinceDataReference({
      code: widget.code,
      field: 'regione',
      label: 'regione provincia',
      name: fieldNames.provinceDataFieldName,
      path,
    }),
    getProvinceDataReference({
      code: widget.code,
      field: 'codiceBelfiore',
      label: 'codice belfiore provincia',
      name: fieldNames.provinceDataFieldName,
      path,
    }),

    // Dati estesi comune
    getTownDataReference({
      code: widget.code,
      field: 'nome',
      label: 'nome comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    getTownDataReference({
      code: widget.code,
      field: 'cap',
      label: 'CAP comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    getTownDataReference({
      code: widget.code,
      field: 'codiceBelfiore',
      label: 'codice belfiore comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    getTownDataReference({
      code: widget.code,
      field: 'codiceIstat',
      label: 'codice Istat comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    getTownDataReference({
      code: widget.code,
      field: 'lat',
      label: 'latitudine comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    getTownDataReference({
      code: widget.code,
      field: 'lng',
      label: 'longitudine comune',
      name: fieldNames.townDataFieldName,
      path,
    }),
    // Provincia e regione sono già nella selezione della provincia
  ];
}

const getProvinceDataReference = getDataReference<keyof Provincia>;
const getTownDataReference = getDataReference<keyof Comune>;

function getDataReference<T extends string>(props: {
  path: IReferencePath;
  name: string;
  code: string;
  label: string;
  field: T;
}): IReference {
  const { path, name, code, field, label } = props;

  return {
    type: ReferenceType.Widget,
    widgetType: null,
    path: [
      ...path,
      {
        direction: ReferencePathDirection.Child,
        code: name,
      },
    ],
    id: `${name}-${field}`,
    code: field,
    label: `${code}: ${label}`,
    field: {
      type: Datatype.String,
    },
  };
}
